/**
 * Auto-pattern suggestion from commit history.
 *
 * Walks the last N commits, classifies each one with the same task-type detector
 * `cai pattern capture` already uses, then groups commits by type. If the same
 * type recurs ≥ MIN_OCCURRENCES times within RECENT_DAYS days, we treat it as a
 * recurring workflow that deserves a pattern.
 *
 * Compared to capture (single commit → draft), this looks at *trends*: which
 * tasks does the user actually do over and over again, and what files do they
 * touch every time?
 */
import { execFileSync } from "node:child_process";
import { type TaskType } from "./capture.js";

/**
 * File-only task type heuristic for cluster classification.
 *
 * Cheaper than the full `detectTaskType` from capture.ts (which also inspects
 * the diff). Cluster analysis runs across many commits — we deliberately stay
 * cheap and accept some false positives. The user reviews the result anyway.
 */
function detectTaskTypeFromFiles(files: string[]): TaskType {
  const f = files.join(" ").toLowerCase();

  if (/auth|login|logout|session|token|jwt|oauth|password/.test(f)) return "auth-flow";
  if (/route|controller|handler|endpoint|\bapi\b/.test(f)) return "api-endpoint";
  if (/model|schema|entity|migration|prisma/.test(f)) return "data-model";
  if (/\.test\.|\.spec\.|__tests?__|^tests?\//m.test(f)) return "test-coverage";
  if (/config|\.env|settings|dockerfile|\.yaml$|\.yml$/.test(f)) return "config-change";

  if (files.length >= 3) return "new-feature";
  return "general-change";
}

export interface CommitInfo {
  hash: string;
  date: string;
  subject: string;
  files: string[];
  taskType: TaskType;
}

export interface PatternCluster {
  taskType: TaskType;
  commits: CommitInfo[];
  /** Files that appear in ≥ 50% of the commits in this cluster */
  commonFiles: string[];
  /** Most recent commit date in the cluster */
  lastSeen: string;
}

const MIN_OCCURRENCES = 3;
const RECENT_DAYS = 30;
const MAX_COMMITS_TO_SCAN = 50;

function git(cwd: string, args: string[]): string {
  try {
    return execFileSync("git", args, {
      cwd,
      encoding: "utf8",
      stdio: ["ignore", "pipe", "ignore"],
      maxBuffer: 8 * 1024 * 1024,
    });
  } catch {
    return "";
  }
}

/**
 * Read recent commits with their changed files. Single git call per commit
 * is too slow — we use one `git log --name-only` and parse the output.
 */
export function readRecentCommits(projectRoot: string, maxCount = MAX_COMMITS_TO_SCAN): CommitInfo[] {
  // Use a marker prefix on the header line so we can split reliably regardless
  // of how git formats blank lines around --name-only output.
  const sep = "\x1f";
  const marker = "\x02CAICOMMIT\x02";
  const out = git(projectRoot, [
    "log",
    `-${maxCount}`,
    "--name-only",
    `--format=${marker}%H${sep}%aI${sep}%s`,
  ]);
  if (!out.trim()) return [];

  const commits: CommitInfo[] = [];
  const blocks = out.split(marker).filter((b) => b.trim());

  for (const block of blocks) {
    const lines = block.split("\n").filter(Boolean);
    if (lines.length === 0) continue;

    const headerParts = lines[0].split(sep);
    if (headerParts.length < 3) continue;
    const [hash, date, subject] = headerParts;
    const files = lines.slice(1);

    const taskType = detectTaskTypeFromFiles(files);
    commits.push({ hash: hash.slice(0, 7), date, subject, files, taskType });
  }

  return commits;
}

/**
 * Group commits by task type and surface clusters that recur often enough
 * within the recent window to be worth proposing as patterns.
 */
export function clusterCommits(commits: CommitInfo[]): PatternCluster[] {
  const cutoff = Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;
  const recent = commits.filter((c) => new Date(c.date).getTime() >= cutoff);

  const byType = new Map<TaskType, CommitInfo[]>();
  for (const c of recent) {
    if (c.taskType === "general-change") continue; // too noisy to cluster
    const list = byType.get(c.taskType) ?? [];
    list.push(c);
    byType.set(c.taskType, list);
  }

  const clusters: PatternCluster[] = [];
  for (const [taskType, group] of byType) {
    if (group.length < MIN_OCCURRENCES) continue;

    const commonFiles = extractCommonFiles(group);
    const lastSeen = group
      .map((c) => c.date)
      .sort((a, b) => b.localeCompare(a))[0];

    clusters.push({ taskType, commits: group, commonFiles, lastSeen });
  }

  // Most recurring first, then most recent
  return clusters.sort((a, b) => {
    if (b.commits.length !== a.commits.length) return b.commits.length - a.commits.length;
    return b.lastSeen.localeCompare(a.lastSeen);
  });
}

/**
 * Files that appear in more than half the cluster's commits, sorted by frequency.
 * "More than half" not "at least half" — otherwise pairs of unique files would
 * always look common at threshold = 1.
 */
export function extractCommonFiles(commits: CommitInfo[]): string[] {
  const counts = new Map<string, number>();
  for (const c of commits) {
    for (const f of c.files) {
      counts.set(f, (counts.get(f) ?? 0) + 1);
    }
  }
  const threshold = Math.floor(commits.length / 2) + 1;
  return Array.from(counts.entries())
    .filter(([, n]) => n >= threshold)
    .sort((a, b) => b[1] - a[1])
    .map(([f]) => f);
}

